import { convex } from "@/lib/convexClient";
import { api } from "../../../convex/_generated/api";

type Bucket = "staging" | "exports";

const toError = (message: string) => ({ message });

export const CloudStorageClient = {
  async upload(
    bucket: Bucket,
    path: string,
    file: File | Blob,
    options?: { cacheControl?: string; upsert?: boolean },
  ) {
    try {
      const { uploadUrl } = await convex.mutation(api.storageObjects.createUploadUrl, {});

      const uploadResponse = await fetch(uploadUrl, {
        method: "POST",
        headers: {
          "Content-Type": (file as File).type || "application/octet-stream",
        },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
      }

      const uploadResult = (await uploadResponse.json()) as { storageId?: string };
      if (!uploadResult.storageId) {
        throw new Error("Upload response did not include storageId");
      }

      const registerResult = await convex.mutation(api.storageObjects.registerUpload, {
        bucket,
        path,
        storageId: uploadResult.storageId,
        size: typeof (file as Blob).size === "number" ? (file as Blob).size : undefined,
        contentType: (file as File).type || undefined,
        upsert: options?.upsert ?? false,
      });

      return {
        data: {
          path: registerResult.path,
          fullPath: registerResult.path,
          id: uploadResult.storageId,
        },
        error: null,
      };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Upload failed";
      return { data: null, error: toError(message) };
    }
  },

  async createSignedUrl(bucket: Bucket, path: string, expiresIn: number) {
    try {
      const signedUrl = await convex.query(api.storageObjects.getPrivateUrlByPath, {
        bucket,
        path,
        expiresIn,
      });

      if (!signedUrl) {
        return { data: null, error: toError(`Object not found: ${path}`) };
      }

      return { data: { signedUrl }, error: null };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Failed to create signed URL";
      return { data: null, error: toError(message) };
    }
  },

  async getPublicUrl(bucket: Bucket, path: string) {
    const siteUrl = import.meta.env.VITE_CONVEX_SITE_URL as string | undefined;
    if (!siteUrl) return { data: { publicUrl: "" } };
    const baseUrl = siteUrl.replace(/\/$/, "");
    const publicUrl = `${baseUrl}/storage/public?bucket=${encodeURIComponent(bucket)}&path=${encodeURIComponent(path)}`;
    return { data: { publicUrl } };
  },

  async remove(bucket: Bucket, paths: string[]) {
    try {
      await convex.mutation(api.storageObjects.removePaths, { bucket, paths });
      return { data: null, error: null };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Failed to remove files";
      return { data: null, error: toError(message) };
    }
  },

  async list(bucket: Bucket, path: string) {
    try {
      const data = (await convex.query(api.storageObjects.listByPrefix, {
        bucket,
        path,
      })) as unknown[];
      return { data, error: null };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Failed to list files";
      return { data: null, error: toError(message) };
    }
  },

  async download(bucket: Bucket, path: string) {
    try {
      const signedUrl = await convex.query(api.storageObjects.getPrivateUrlByPath, {
        bucket,
        path,
        expiresIn: 3600,
      });

      if (!signedUrl) {
        return { data: null, error: toError(`Object not found: ${path}`) };
      }

      const response = await fetch(signedUrl);
      if (!response.ok) {
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.blob();
      return { data, error: null };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Failed to download file";
      return { data: null, error: toError(message) };
    }
  },

  async createSignedUploadUrl(path: string) {
    try {
      const { uploadUrl } = await convex.mutation(api.storageObjects.createUploadUrl, {});
      return {
        data: {
          signedUrl: uploadUrl,
          path,
          token: "",
        },
        error: null,
      };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Failed to create signed upload URL";
      return { data: null, error: toError(message) };
    }
  },
};
